import { useState, useEffect, useCallback, useRef } from "react";
import {
  NetworkInformationAPI,
  NetworkMetrics,
  NetworkMonitorConfig,
  UseNetworkMonitorReturn,
} from "../components/network-status/networkStatusTypes";
import { DEFAULT_NETWORK_MONITOR_CONFIG } from "../components/network-status/networkStatusConstants";
import {
  evaluateNetworkQuality,
  getBrowserConnectionInfo,
  measureProbeLatency,
} from "../utils/networkMonitorUtils";

// --- Global Shared Singleton State ---
let sharedMetrics: NetworkMetrics = {
  status:
    typeof navigator !== "undefined" && navigator.onLine ? "GOOD" : "OFFLINE",
  isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
  downlinkSpeedMbps: getBrowserConnectionInfo()?.downlink,
  expectedSpeedMbps: DEFAULT_NETWORK_MONITOR_CONFIG.expectedSpeedMbps,
  speedReductionPercent: 0,
  effectiveType: getBrowserConnectionInfo()?.effectiveType,
  rttMs: getBrowserConnectionInfo()?.rtt,
  lastCheckedAt: Date.now(),
};

let lastProbeTime = 0;
let isProbing = false;
let consecutiveSlowCount = 0;
const subscribers = new Set<(metrics: NetworkMetrics) => void>();
let globalTimer: NodeJS.Timeout | null = null;
let isGlobalListenersAttached = false;

const notifySubscribers = () => {
  subscribers.forEach((callback) => callback({ ...sharedMetrics }));
};

const runSharedEvaluation = async (
  config: NetworkMonitorConfig = DEFAULT_NETWORK_MONITOR_CONFIG,
  force: boolean = false,
) => {
  const now = Date.now();
  // Prevent parallel or overly frequent probes (minimum 2s between network requests unless forced)
  if (isProbing || (!force && now - lastProbeTime < 2000)) {
    return;
  }

  isProbing = true;
  lastProbeTime = now;

  try {
    const rawOnline =
      typeof navigator !== "undefined" ? navigator.onLine : true;

    if (!rawOnline) {
      consecutiveSlowCount = 0;
      sharedMetrics = {
        status: "OFFLINE",
        isOnline: false,
        speedReductionPercent: 100,
        expectedSpeedMbps: config.expectedSpeedMbps,
        lastCheckedAt: Date.now(),
      };
      notifySubscribers();
      return;
    }

    const connInfo = getBrowserConnectionInfo();
    const probe = await measureProbeLatency(config.pingEndpoint);

    // If probe failed completely even though rawOnline was true (e.g., LAN connected but ISP/Internet down)
    if (!probe.ok) {
      consecutiveSlowCount = 0;
      sharedMetrics = {
        status: "OFFLINE",
        isOnline: false,
        speedReductionPercent: 100,
        expectedSpeedMbps: config.expectedSpeedMbps,
        lastCheckedAt: Date.now(),
      };
      notifySubscribers();
      return;
    }

    const quality = evaluateNetworkQuality(
      true,
      connInfo,
      probe.latencyMs,
      config,
    );

    if (quality.status === "SLOW" || quality.status === "POOR") {
      consecutiveSlowCount += 1;
    } else {
      consecutiveSlowCount = 0;
    }

    sharedMetrics = {
      status: quality.status,
      isOnline: true,
      downlinkSpeedMbps: quality.downlinkSpeedMbps,
      speedReductionPercent: quality.speedReductionPercent,
      expectedSpeedMbps: quality.expectedSpeedMbps,
      effectiveType: quality.effectiveType,
      rttMs: quality.rttMs,
      lastCheckedAt: Date.now(),
    };

    notifySubscribers();
  } finally {
    isProbing = false;
  }
};

const setupGlobalListeners = (config: NetworkMonitorConfig) => {
  if (isGlobalListenersAttached || typeof window === "undefined") return;
  isGlobalListenersAttached = true;

  const handleOnline = () => {
    // 1. Immediately update status to ONLINE from native browser event (0ms instant UI update, 0 waiting)
    sharedMetrics = {
      ...sharedMetrics,
      status: "GOOD",
      isOnline: true,
      speedReductionPercent: 0,
      lastCheckedAt: Date.now(),
    };
    notifySubscribers();

    // 2. Asynchronously refine speed metrics in background
    runSharedEvaluation(config, true);
  };

  const handleOffline = () => {
    // Immediately update status to OFFLINE from native browser event (0ms instant UI update)
    consecutiveSlowCount = 0;
    sharedMetrics = {
      status: "OFFLINE",
      isOnline: false,
      speedReductionPercent: 100,
      expectedSpeedMbps: config.expectedSpeedMbps,
      lastCheckedAt: Date.now(),
    };
    notifySubscribers();
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      runSharedEvaluation(config);
    }
  };

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);
  window.addEventListener("focus", handleOnline);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  const navConn = (
    navigator as unknown as { connection?: NetworkInformationAPI }
  ).connection;
  if (navConn?.addEventListener) {
    navConn.addEventListener("change", handleOnline);
  }

  // Initial evaluation on startup
  runSharedEvaluation(config);

  // Single global heartbeat timer with randomized jitter to prevent server request clustering
  const scheduleNextProbe = () => {
    if (globalTimer) clearTimeout(globalTimer);
    const jitter = (Math.random() * 0.4 - 0.2) * config.pingIntervalMs; // +/- 20% jitter (e.g. 12s to 18s)
    const nextDelay = Math.max(
      5000,
      Math.round(config.pingIntervalMs + jitter),
    );

    globalTimer = setTimeout(async () => {
      if (document.visibilityState === "visible") {
        await runSharedEvaluation(config);
      }
      scheduleNextProbe();
    }, nextDelay);
  };

  scheduleNextProbe();
};

/**
 * Custom React hook for real-time network status and speed quality monitoring.
 * Uses a singleton subscriber pattern to ensure only ONE timer and probe is active across the entire application.
 */
export const useNetworkMonitor = (
  customConfig?: Partial<NetworkMonitorConfig>,
): UseNetworkMonitorReturn => {
  const configRef = useRef<NetworkMonitorConfig>({
    ...DEFAULT_NETWORK_MONITOR_CONFIG,
    ...customConfig,
  });

  const [metrics, setMetrics] = useState<NetworkMetrics>(sharedMetrics);

  useEffect(() => {
    setupGlobalListeners(configRef.current);

    const handleUpdate = (latest: NetworkMetrics) => {
      setMetrics(latest);
    };

    subscribers.add(handleUpdate);
    return () => {
      subscribers.delete(handleUpdate);
    };
  }, []);

  const checkNow = useCallback(async () => {
    await runSharedEvaluation(configRef.current, true);
  }, []);

  return {
    ...metrics,
    checkNow,
    config: configRef.current,
  };
};

export default useNetworkMonitor;
